import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUpdateResellerProfile } from "@/api/reseller/mutations";
import { useResellerMyProfile } from "@/api/reseller/queries";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/auth-context";

export function ResellerPersonalizationTab() {
	const { t } = useTranslation();
	const { updateUser } = useAuth();
	const { data, isLoading } = useResellerMyProfile();
	const updateProfile = useUpdateResellerProfile();
	const emptyForm = {
		company_name: "",
		primary_color: "#000000",
		secondary_color: "#000000",
		hide_electricity: false,
	};
	const [form, setForm] = useState(emptyForm);
	const [initialForm, setInitialForm] = useState(emptyForm);
	const prevDataRef = useRef<typeof data>(undefined);

	if (data && data !== prevDataRef.current) {
		prevDataRef.current = data;
		const loaded = {
			company_name: data.company_name ?? "",
			primary_color: data.primary_color || "#000000",
			secondary_color: data.secondary_color || "#000000",
			hide_electricity: data.hide_electricity ?? false,
		};
		setForm(loaded);
		setInitialForm(loaded);
	}

	const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);

	function handleSave() {
		updateProfile.mutate(form, {
			onSuccess: () => {
				toast.success(t("settings.profile.saved"));
				updateUser({
					primary_color: form.primary_color,
					secondary_color: form.secondary_color,
				});
			},
			onError: () => toast.error(t("settings.profile.saveError")),
		});
	}

	if (isLoading) {
		return (
			<Card>
				<CardContent className="flex flex-col gap-4 pt-6">
					<Skeleton className="h-5 w-32" />
					<Skeleton className="h-10 w-full" />
					<Skeleton className="h-10 w-full" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className="flex flex-col gap-6 pt-6">
				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.profile.companyName")}
					</p>
					<div className="grid gap-2">
						<Label htmlFor="reseller-company">
							{t("settings.profile.companyName")}
						</Label>
						<Input
							id="reseller-company"
							autoComplete="organization"
							name="company-name"
							value={form.company_name}
							onChange={(e) =>
								setForm((f) => ({ ...f, company_name: e.target.value }))
							}
						/>
					</div>
				</div>

				<Separator />

				<div className="flex flex-col gap-3">
					<p className="text-sm font-medium text-muted-foreground">
						{t("settings.company.appearance")}
					</p>
					{data?.logo && (
						<div className="grid gap-2">
							<Label>{t("settings.company.logo")}</Label>
							<img
								src={data.logo}
								alt={t("settings.company.logo")}
								className="h-12 w-auto object-contain rounded border p-1.5"
							/>
						</div>
					)}
					<div className="flex gap-6">
						<div className="grid gap-2">
							<Label htmlFor="reseller-primary-color">
								{t("settings.company.primaryColor")}
							</Label>
							<div className="flex items-center gap-2">
								<input
									id="reseller-primary-color"
									type="color"
									name="primary-color"
									value={form.primary_color}
									onChange={(e) =>
										setForm((f) => ({ ...f, primary_color: e.target.value }))
									}
									className="size-9 cursor-pointer rounded border p-0.5"
								/>
								<span className="text-sm text-muted-foreground font-mono">
									{form.primary_color}
								</span>
							</div>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="reseller-secondary-color">
								{t("settings.company.secondaryColor")}
							</Label>
							<div className="flex items-center gap-2">
								<input
									id="reseller-secondary-color"
									type="color"
									name="secondary-color"
									value={form.secondary_color}
									onChange={(e) =>
										setForm((f) => ({
											...f,
											secondary_color: e.target.value,
										}))
									}
									className="size-9 cursor-pointer rounded border p-0.5"
								/>
								<span className="text-sm text-muted-foreground font-mono">
									{form.secondary_color}
								</span>
							</div>
						</div>
					</div>
				</div>

				<div className="flex justify-end gap-2">
					<Button
						size="sm"
						variant="outline"
						onClick={() => setForm(initialForm)}
						disabled={!isDirty || updateProfile.isPending}
					>
						{t("settings.profile.cancel")}
					</Button>
					<Button
						size="sm"
						onClick={handleSave}
						disabled={updateProfile.isPending || !isDirty}
					>
						{updateProfile.isPending
							? t("settings.profile.saving")
							: t("settings.profile.save")}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}
